import { SendIntent } from 'send-intent'
import { Capacitor } from '@capacitor/core'
import bus from './eventbus'
import router from './router'

const URL_RE = /(https?:\/\/[^\s]+)/g

// Android's SendIntent plugin maps EXTRA_TEXT to the `url` field for text/plain
// shares (see node_modules/send-intent/.../SendIntent.java:66-68). Chrome on
// Android shares text selections as "<selected text>\n\nhttps://<url>" or
// "<selected text> including link: <url>". If we naively look for a URL inside
// `intent.url`, we route those text shares to /bookmarks/add. Use this
// heuristic to detect "this is primarily text, with a URL appended" and treat
// it as a NOTE share instead.
function looksLikePrimarilyText(value) {
  if (!value) return false
  const trimmed = value.trim()
  if (!trimmed) return false

  // Definitive marker: Chrome's "including link:" suffix.
  if (/\bincluding link:?\s+https?:\/\//i.test(trimmed)) return true

  const urlMatch = trimmed.match(URL_RE)
  if (!urlMatch) return true // pure text, no URL → trivially "primarily text"

  const url = urlMatch[0]
  if (trimmed === url) return false // pure URL share

  // Strip the URL and "including link:" markers, then check what's left.
  const remainder = trimmed
    .replace(url, '')
    .replace(/\bincluding link:?\b/gi, '')
    .replace(/\s+/g, ' ')
    .trim()

  // If remainder has any alphanumeric content and is more than a few chars,
  // treat as primarily text. Page-title shares typically have remainder ≤ ~80
  // chars but Chrome page-only shares put just the URL in EXTRA_TEXT, so most
  // page-title-prefixed remainders here mean the user shared a text selection.
  if (!/[a-zA-Z0-9]/.test(remainder)) return false
  return remainder.length >= 3
}

// Strip the "including link: <url>" or trailing "<url>" reference that Chrome
// (and similar share apps) appends to selected text. Used for the note path
// so the saved note content is just the text the user actually selected.
function stripUrlReferenceFromText(text) {
  if (!text) return ''
  let result = text
  // "<text>\n\nincluding link:\n<url>" or "<text>\n\n<url>"
  result = result.replace(/\s*\n+\s*(including link:?\s*\n*\s*)?https?:\/\/\S+\s*$/i, '')
  // "<text> including link: <url>"
  result = result.replace(/\s+including link:?\s+https?:\/\/\S+\s*$/i, '')
  // Bare trailing URL with whitespace
  result = result.replace(/\s+https?:\/\/\S+\s*$/, '')
  return result.trim()
}

function extractTitle(titleString) {
  if (!titleString) return null

  let title = titleString
  // Try to decode the title, but don't fail if it contains invalid percent encoding
  try {
    title = decodeURIComponent(titleString)
  } catch (e) {
    // If decoding fails (e.g., "30%" in title), just use the original string
    title = titleString
  }

  // if we find a URL, delete it
  const titleWithoutUrls = title.replaceAll(URL_RE, '').trim()

  return titleWithoutUrls || null
}

function extractUrlFromIntent(intent) {
  // Helper function to clean and normalize URLs
  const cleanUrl = url => {
    if (!url) return null

    // Remove any zero-width spaces or invisible characters
    let cleanedUrl = url.replace(/[\u200B-\u200D\uFEFF]/g, '')

    // Trim whitespace
    cleanedUrl = cleanedUrl.trim()

    // Handle potential Android intent URL encoding issues
    // Sometimes URLs come with android-app:// prefix or other wrappers
    if (cleanedUrl.includes('http://') || cleanedUrl.includes('https://')) {
      const match = cleanedUrl.match(/(https?:\/\/[^\s)]+)/i)
      if (match) {
        const [matchedUrl] = match
        cleanedUrl = matchedUrl
      }
    }

    return cleanedUrl
  }

  // Direct URL field. On Android, EXTRA_TEXT lands here for text/plain shares,
  // so guard with looksLikePrimarilyText to avoid routing text shares to /bookmarks.
  if (intent.url && !looksLikePrimarilyText(intent.url)) {
    const cleaned = cleanUrl(intent.url)
    if (cleaned) {
      try {
        const validUrl = new URL(cleaned)
        return validUrl.href
      } catch {
        const extractedUrl = cleaned.match(URL_RE)?.[0]
        if (extractedUrl) return extractedUrl
      }
    }
  }

  if (intent.additionalItems?.[0]?.url && !looksLikePrimarilyText(intent.additionalItems[0].url)) {
    const cleaned = cleanUrl(intent.additionalItems[0].url)
    if (cleaned) {
      try {
        const decodedUrl = decodeURIComponent(cleaned)
        const validUrl = new URL(decodedUrl)
        return validUrl.href
      } catch {
        try {
          const validUrl = new URL(cleaned)
          return validUrl.href
        } catch {
          const extractedUrl = cleaned.match(URL_RE)?.[0]
          if (extractedUrl) return extractedUrl
        }
      }
    }
  }

  // Text field: only treat as URL share when text is primarily a URL (Steam,
  // AliExpress, Amazon-style sharing where the text IS just the URL).
  if (intent.text && !looksLikePrimarilyText(intent.text)) {
    const cleaned = cleanUrl(intent.text)
    if (cleaned) {
      const textUrl = cleaned.match(URL_RE)?.[0]
      if (textUrl) return textUrl
    }
  }

  if (intent.additionalItems?.[0]?.text && !looksLikePrimarilyText(intent.additionalItems[0].text)) {
    const cleaned = cleanUrl(intent.additionalItems[0].text)
    if (cleaned) {
      const itemTextUrl = cleaned.match(URL_RE)?.[0]
      if (itemTextUrl) return itemTextUrl
    }
  }

  // Title fallback — only if the title is primarily a URL (not text + URL).
  const title = intent.title || intent.additionalItems?.[0]?.title
  if (!title || looksLikePrimarilyText(title)) return null

  const cleaned = cleanUrl(title)
  if (!cleaned) return null

  try {
    const decodedUrl = decodeURIComponent(cleaned)
    const url = decodedUrl.match(URL_RE)?.[0]
    return url || null
  } catch {
    const url = cleaned.match(URL_RE)?.[0]
    return url || null
  }
}

async function checkIntent(app) {
  console.log('Checking Intent')

  try {
    const result = await SendIntent.checkSendIntentReceived()

    if (!result) {
      console.error('No SendIntent found')
      return
    }

    console.log('SendIntent found', JSON.stringify(result))

    // Enhanced logging for debugging intents
    console.log('Intent fields:', {
      url: result.url,
      text: result.text,
      title: result.title,
      type: result.type,
      additionalItems: result.additionalItems,
    })

    const payload = result.url ? result : result.additionalItems?.[0]
    const url = extractUrlFromIntent(result)

    if (!url) {
      // Read text from any of the populated fields. Android's SendIntent plugin
      // maps EXTRA_TEXT to `url` for text/plain shares (see SendIntent.java),
      // so result.url often holds the actual selected text. result.text is for
      // platforms that populate it; result.content is custom; result.title is
      // EXTRA_SUBJECT (page title) on Android.
      const rawText = (
        result.content
        || result.text
        || result.url
        || result.additionalItems?.[0]?.text
        || result.additionalItems?.[0]?.url
        || result.title
        || ''
      )
      const intentType = result.type || result.additionalItems?.[0]?.type || ''

      // Strip Chrome's "including link: <url>" / trailing-URL share markers
      // so the saved note shows just the user's selection, not the page URL.
      const cleanedText = stripUrlReferenceFromText(rawText) || rawText.trim()

      if (cleanedText.trim() && (intentType === 'text/plain' || !intentType)) {
        console.log('Text-only intent detected, routing to note creation')

        const noteTitle = cleanedText.trim().substring(0, 100)

        app.config.globalProperties.$store.dispatch('native/onNewIntent', {
          type: 'note',
          text: cleanedText,
          title: noteTitle,
          result: {
            ...result,
            title: noteTitle,
            text: cleanedText,
            type: 'text/plain',
          },
          finish: () => {
            SendIntent.finish()
            app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
          },
        })

        router.replace('/notes/add').catch(() => {})
        return
      }

      console.error('No URL found in intent and not a text share', result)
      bus.emit('toast:error', 'Could not process shared content')
      return
    }

    console.log('Extracted URL:', url)

    // Try to create URL object without decoding first (might already be decoded)
    let urlObject
    try {
      urlObject = new URL(url)
    } catch (firstError) {
      // If that fails, try with decoding
      try {
        urlObject = new URL(decodeURIComponent(url))
      } catch (secondError) {
        // If both fail, just use the string as-is if it looks like a URL
        if (url.match(/^https?:\/\//)) {
          urlObject = { toString: () => url }
        } else {
          console.error('Error resolving URL', url, 'First:', firstError.message, 'Second:', secondError.message)
          bus.emit('toast:error', 'Could not process shared link')
          return
        }
      }
    }

    if (urlObject) {
      const parsedData = {
        ...payload,
        title: extractTitle(payload?.title || result.title)?.slice(0, 100) ?? undefined, // TODO 100 should be a const
        url: urlObject.toString(),
      }

      app.config.globalProperties.$store.dispatch('native/onNewIntent', {
        url: parsedData.url,
        result: parsedData,
        finish: () => {
          //if (Capacitor.getPlatform() === 'android') {
            SendIntent.finish()
          //}
          app.config.globalProperties.$store.dispatch('native/onNewIntent', null)
        },
      })

      router.replace('/bookmarks/add').catch(() => {})
    }
  } catch (error) {
    console.error('Error handling sendIntent:', error, error.message)
    // Show user-friendly error message
    bus.emit('toast:error', 'Could not process shared content')
  }
}

bus.$once('app:created', async app => {
  // iOS shares are now handled in store/modules/native/index.js via the appUrlOpen
  // listener — that path is more reliable than SendIntent's notification → bridge.eval
  // → window-event chain on iOS 26+. Keep this path active for Android (which delivers
  // shares via native Intents, not URL schemes).
  if (app.config.globalProperties.$isNative && Capacitor.getPlatform() !== 'ios') {
    checkIntent(app)

    window.addEventListener('sendIntentReceived', async () => {
      console.log('sendIntent received')
      checkIntent(app)
    })
  }
})
